import React from 'react';
import { View, StyleSheet, Modal, Pressable, ScrollView, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography, Button } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';

import { ACCENT_TEXT } from '../../styles/accentText';
interface Props {
  visible: boolean;
  /** Текст про полную приватность — приходит с сервера вместе с вопросами. */
  privacyHint: string;
  /** Выбор человека: читает ли Агапелла его записи. */
  onChoose: (aiMayRead: boolean) => void;
  busy?: boolean;
}

/**
 * CORE-1455 — окно, которое человек видит один раз.
 *
 * Надежда, 22.09: «удобнее реализовать через всплывающее окно, где пользователь
 * сразу читает всю информацию про фичу и сохраняет настройки. Изменить их он
 * может при необходимости. Чтобы каждый раз при новой рефлексии не указывать
 * настройки конфиденциальности».
 *
 * Поэтому здесь нет крестика и нет «решу потом». Выбор про приватность — это
 * развилка, от которой зависит, куда попадёт написанное; отложенный выбор
 * означал бы, что первая запись сохранится по умолчанию, которое человек не
 * выбирал. Закрыть окно можно только выбрав, зато ровно один раз: дальше
 * настройка живёт на сервере и меняется в настройках.
 *
 * Обе кнопки равноправны намеренно. Сделать «ИИ читает» заметнее значило бы
 * подтолкнуть к тому варианту, где человек отдаёт больше, — в окне про
 * приватность это нечестно.
 */
export function ReflectionPrivacyModal({ visible, privacyHint, onChoose, busy = false }: Props) {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { colors } = useTheme();
  // CORE-1751 — окно открывается и в приложении, где язык бывает не русским.
  const { t } = useI18n('dashboard');

  if (!visible) return null;

  const body = (
    <View style={styles.backdrop}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* CORE-1751 — фон без альфы (`surfaceDialog`, CORE-1133): на полупрозрачном
            surfaceHover сквозь карточку читались плеер и название практики. */}
        <View style={[styles.card, isDesktop && styles.cardDesktop, { backgroundColor: colors.surfaceDialog }]}>
          <View style={styles.iconCircle}>
            <Ionicons name="book-outline" size={26} color={ACCENT_TEXT} />
          </View>

          <Typography variant="h3" style={[styles.heading, { color: colors.textPrimary }]}>
            {t('journal.privacy.title')}
          </Typography>

          <Typography variant="body" style={[styles.body, { color: colors.textPrimary }]}>
            {t('journal.privacy.intro')}
          </Typography>

          <Typography variant="body" style={[styles.body, { color: colors.textPrimary }]}>
            {t('journal.privacy.readers', { privacyHint })}
          </Typography>

          <Typography variant="caption" style={[styles.note, { color: colors.textSecondary }]}>
            {t('journal.privacy.note')}
          </Typography>

          <View style={styles.buttons}>
            <Button
              testID="reflection-privacy-ai"
              variant="convex-fire"
              onPress={() => onChoose(true)}
              disabled={busy}
              style={styles.btn}
            >
              {t('journal.privacy.aiMayRead')}
            </Button>
            <Button
              testID="reflection-privacy-private"
              variant="secondary"
              onPress={() => onChoose(false)}
              disabled={busy}
              style={styles.btn}
            >
              {t('journal.privacy.privateToMe')}
            </Button>
          </View>
        </View>
      </ScrollView>
    </View>
  );

  return (
    // Закрытия по фону и по кнопке «назад» нет: выбор обязателен, см. заголовок.
    <Modal visible transparent animationType="fade" onRequestClose={() => {}}>
      {body}
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)' },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1, justifyContent: 'center', padding: 20 },
  card: { borderRadius: 20, padding: 24, gap: 12 },
  cardDesktop: { maxWidth: 460, alignSelf: 'center', width: '100%' },
  iconCircle: {
    width: 52, height: 52, borderRadius: 26, alignItems: 'center',
    justifyContent: 'center', backgroundColor: 'rgba(233,130,69,0.08)',
  },
  heading: { marginTop: 4 },
  body: { lineHeight: 22 },
  note: { lineHeight: 18 },
  buttons: { gap: 10, marginTop: 8 },
  btn: { width: '100%' },
});
